import {Component, inject, input, ChangeDetectionStrategy} from "@angular/core"
import {MatMenuModule} from "@angular/material/menu"
import {MatTooltipModule} from "@angular/material/tooltip"
import {ToggleComponent} from "@app/common/components/buttons/toggle/toggle.component"
import {ObjectNode} from "@cm/template-nodes"
import {TemplateMenuComponent} from "@app/template-editor/components/template-menu/template-menu.component"
import {TemplateMenuSectionComponent} from "@app/template-editor/components/template-menu-section/template-menu-section.component"
import {ListItemComponent} from "@app/common/components/item"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {Matrix4} from "@cm/math"

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-template-tree-object-transform",
    imports: [MatMenuModule, ToggleComponent, MatTooltipModule, TemplateMenuComponent, TemplateMenuSectionComponent, ListItemComponent],
    templateUrl: "./template-tree-object-transform.component.html",
    styleUrl: "./template-tree-object-transform.component.scss",
})
export class TemplateTreeObjectTransformComponent {
    readonly $object = input.required<ObjectNode>({alias: "object"})
    readonly $processingNodes = input<ObjectNode[] | undefined>(undefined, {alias: "processingNodes"})

    private readonly sceneManagerService = inject(SceneManagerService)

    private getProcessingNodes() {
        const processingNodes = this.$processingNodes()
        if (processingNodes) return processingNodes

        const object = this.$object()
        return [object]
    }

    resetNodeTransform() {
        const processingNodes = this.getProcessingNodes()
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            processingNodes.forEach((templateNode) => {
                templateNode.updateParameters({
                    lockedTransform: this.sceneManagerService.getISceneManager().defaultTransformForObject(templateNode, false)?.toArray(),
                })
            })
        })
    }

    setTransformLocked(locked: boolean) {
        const processingNodes = this.getProcessingNodes()
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            processingNodes.forEach((templateNode) => {
                if (locked && !templateNode.parameters.lockedTransform) {
                    templateNode.updateParameters({
                        lockedTransform:
                            this.sceneManagerService.getTransformAccessor(templateNode)?.getTransform()?.toArray() ??
                            this.sceneManagerService.getISceneManager().defaultTransformForObject(templateNode, false)?.toArray(),
                    })
                } else if (!locked && templateNode.parameters.lockedTransform) {
                    templateNode.updateParameters({lockedTransform: undefined})
                }
            })
        })
    }

    moveToOrigin() {
        const processingNodes = this.getProcessingNodes()
        if (processingNodes.length === 0) return

        this.sceneManagerService.modifyTemplateGraph(() => {
            const transform = Matrix4.translation(0, 0, 0)
            processingNodes.forEach((templateNode) => {
                if (templateNode.parameters.lockedTransform) templateNode.updateParameters({lockedTransform: transform.toArray()})
            })
        })
    }
}
